import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { Device, Call } from "@twilio/voice-sdk";
import { message } from "antd";
import { getTwilioAccessToken, getMyTwilioNo, recordCallActivity } from "@/services/app/twilio";
import { connectTwilioStatus, sendTwilioHeartbeat, disconnectTwilioStatus } from "@/services/app/tenant/twilio-connection";
import { history, useModel } from "@umijs/max";

// Cross-tab coordination of the CSR "busy" state. Each tab runs its own Twilio
// Device, so an inbound call rings every tab; without coordination an idle tab
// would ring out loud while the CSR is mid-conversation in another tab. Tabs
// announce their busy state over this channel so every tab can silence its
// ringer when ANY tab has an active/held call.
const BUSY_CHANNEL_NAME = "twilio_csr_busy_state";
const BUSY_STATE_EXPIRY_MS = 6000; // ignore remote busy entries older than this (covers crashed tabs)
const BUSY_STATE_REFRESH_MS = 2000; // how often a busy tab re-announces and stale entries are pruned

export interface HeldCallInfo {
  call: Call;
  holdType: "public" | "private";
  phoneNumber: string;
  heldAt: Date;
  /**
   * The customer-leg call SID returned by the upstream hold API response.
   * Used as call_sid when resuming a public hold via the publicTakeover contract,
   * because the browser-SDK call SID is the CSR/assistant leg — not the customer leg.
   * May be undefined if the hold API did not return it.
   */
  customerCallSid?: string;
  /** @internal Auto-cleanup listener attached in moveToHeld — do not use outside TwilioDeviceContext */
  _disconnectHandler?: () => void;
}

interface TwilioDeviceContextType {
  device: Device | null;
  twilioReady: boolean;
  twilioConnecting: boolean;
  myPhoneNumber: string | null;
  incomingCalls: Call[];
  activeCall: Call | null;
  heldCalls: HeldCallInfo[];
  callDirection: "incoming" | "outgoing" | null;
  makeCall: (phoneNumber: string, queueId?: number) => Promise<Call | undefined>;
  acceptIncomingCall: (call: Call) => void;
  rejectIncomingCall: (call: Call) => void;
  hangupCall: () => void;
  clearIncomingCall: (call: Call) => void;
  clearActiveCall: () => void;
  moveToHeld: (call: Call, holdType: "public" | "private", customerCallSid?: string) => void;
  resumeFromHeld: (info: HeldCallInfo) => void;
  /**
   * Find a held call by its Twilio call SID and promote it to the active call.
   * Matches against the browser-leg SID (info.call.parameters.CallSid) first,
   * then falls back to the customer-leg SID (info.customerCallSid) that is
   * stored for public holds.  Returns the matched HeldCallInfo, or null when no
   * match is found (e.g. the call was already removed or SID is wrong).
   *
   * Use this from external components (e.g. HeldCallsPanel) that trigger the
   * resume API call themselves but need to keep the shared context in sync.
   */
  resumeHeldCallBySid: (callSid: string) => HeldCallInfo | null;
  removeFromHeld: (info: HeldCallInfo) => void;
  reconnect: () => void;
}

const TwilioDeviceContext = createContext<TwilioDeviceContextType>({
  device: null,
  twilioReady: false,
  twilioConnecting: false,
  myPhoneNumber: null,
  incomingCalls: [],
  activeCall: null,
  heldCalls: [],
  callDirection: null,
  makeCall: async () => undefined,
  acceptIncomingCall: () => {},
  rejectIncomingCall: () => {},
  hangupCall: () => {},
  clearIncomingCall: () => {},
  clearActiveCall: () => {},
  moveToHeld: () => {},
  resumeFromHeld: () => {},
  resumeHeldCallBySid: () => null,
  removeFromHeld: () => {},
  reconnect: () => {},
});

export const useTwilioDevice = () => useContext(TwilioDeviceContext);

/**
 * Module-level ref so components outside the TwilioDeviceProvider tree
 * (e.g. the nav-bar CallbackBadge in actionsRender) can still invoke makeCall.
 * The provider keeps this updated via useEffect.
 */
export const globalMakeCallRef: React.MutableRefObject<
  ((phoneNumber: string, queueId?: number) => Promise<Call | undefined>) | null
> = { current: null };

export const TwilioDeviceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { initialState } = useModel("@@initialState");
  const [twilioReady, setTwilioReady] = useState(false);
  const [twilioConnecting, setTwilioConnecting] = useState(false);
  const [myPhoneNumber, setMyPhoneNumber] = useState<string | null>(null);
  const [incomingCalls, setIncomingCalls] = useState<Call[]>([]);
  const [activeCall, setActiveCall] = useState<Call | null>(null);
  const [heldCalls, setHeldCalls] = useState<HeldCallInfo[]>([]);
  const [callDirection, setCallDirection] = useState<"incoming" | "outgoing" | null>(null);

  const deviceRef = useRef<Device | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const connectingRef = useRef(false);
  const tokenRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectScheduledRef = useRef(false);
  const offlinePollerRef = useRef<NodeJS.Timeout | null>(null);
  const activeCallRef = useRef<Call | null>(null);
  const incomingCallsRef = useRef<Call[]>([]);
  const heldCallsRef = useRef<HeldCallInfo[]>([]);
  const reconnectRef = useRef<() => void>(() => {});
  const scheduleReconnectRef = useRef<(label: string) => void>(() => {});

  // Cross-tab busy-state coordination (see BUSY_* constants above)
  const busyChannelRef = useRef<BroadcastChannel | null>(null);
  const tabIdRef = useRef<string>(Math.random().toString(36).slice(2) + Date.now().toString(36));
  const remoteBusyTabsRef = useRef<Map<string, number>>(new Map());
  const localBusyRef = useRef<boolean>(false);

  // Tracks whether a disconnect beacon has already been sent from this tab in this session.
  // Prevents both the beforeunload handler and the provider cleanup from firing it twice.
  const disconnectBeaconSentRef = useRef(false);

  // Track this tab in localStorage so we only send a disconnect signal when
  // the LAST open tab closes — not when the user just closes one of several tabs.
  const TAB_REGISTRY_KEY = 'twilio_active_tabs';

  const registerTab = useCallback(() => {
    try {
      const tabs: string[] = JSON.parse(localStorage.getItem(TAB_REGISTRY_KEY) || '[]');
      if (!tabs.includes(tabIdRef.current)) {
        tabs.push(tabIdRef.current);
        localStorage.setItem(TAB_REGISTRY_KEY, JSON.stringify(tabs));
      }
    } catch {}
  }, []);

  // Removes this tab from the registry and returns true if it was the last one.
  const unregisterTab = useCallback((): boolean => {
    try {
      const tabs: string[] = JSON.parse(localStorage.getItem(TAB_REGISTRY_KEY) || '[]');
      const remaining = tabs.filter((id) => id !== tabIdRef.current);
      localStorage.setItem(TAB_REGISTRY_KEY, JSON.stringify(remaining));
      return remaining.length === 0;
    } catch {
      return true; // fail-safe: assume last tab
    }
  }, []);

  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  useEffect(() => {
    incomingCallsRef.current = incomingCalls;
  }, [incomingCalls]);

  useEffect(() => {
    heldCallsRef.current = heldCalls;
  }, [heldCalls]);

  // Broadcast this tab's busy state to the other tabs.
  const broadcastBusyState = useCallback((busy: boolean) => {
    try {
      busyChannelRef.current?.postMessage({ type: "busy", tabId: tabIdRef.current, busy });
    } catch (e) {
      // BroadcastChannel may be unavailable / closing — fail silently
    }
  }, []);

  // Silence the built-in incoming ringtone whenever the CSR is busy on a call
  // (active or held) in ANY open tab, so a second inbound call doesn't ring in
  // their ear while they're speaking to a customer. The on-screen incoming-call
  // card still appears so they can choose to hold and answer. The ringer is
  // restored as soon as every tab is completely free.
  const applyRingerState = useCallback(() => {
    const audio = deviceRef.current?.audio;
    if (!audio) return;
    const now = Date.now();
    let remoteBusy = false;
    remoteBusyTabsRef.current.forEach((ts) => {
      if (now - ts < BUSY_STATE_EXPIRY_MS) remoteBusy = true;
    });
    const isBusy = localBusyRef.current || remoteBusy;
    try {
      audio.incoming(!isBusy);
    } catch (e) {
      // audio.incoming() may not be available in all SDK versions — fail silently
      console.warn("Could not toggle Twilio incoming ringtone:", e);
    }
  }, []);

  // Track this tab's own busy state, tell the other tabs, and re-apply the ringer.
  useEffect(() => {
    const isBusy = !!activeCall || heldCalls.length > 0;
    localBusyRef.current = isBusy;
    broadcastBusyState(isBusy);
    applyRingerState();
  }, [activeCall, heldCalls, broadcastBusyState, applyRingerState]);

  // Wire up the cross-tab BroadcastChannel: listen for other tabs' busy state,
  // answer busy-state queries from newly opened tabs, periodically prune stale
  // entries (tabs that closed/crashed without announcing) and re-announce our
  // own state so peers stay fresh.
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return; // older browsers — no cross-tab sync
    const channel = new BroadcastChannel(BUSY_CHANNEL_NAME);
    busyChannelRef.current = channel;

    channel.onmessage = (event: MessageEvent) => {
      const data = event?.data;
      if (!data || data.tabId === tabIdRef.current) return;
      if (data.type === "busy") {
        if (data.busy) remoteBusyTabsRef.current.set(data.tabId, Date.now());
        else remoteBusyTabsRef.current.delete(data.tabId);
        applyRingerState();
      } else if (data.type === "query") {
        // A freshly opened tab is asking who's busy — answer if we are.
        if (localBusyRef.current) broadcastBusyState(true);
      }
    };

    // Ask existing tabs for their busy state so this tab silences immediately
    // instead of waiting for the next refresh tick.
    try {
      channel.postMessage({ type: "query", tabId: tabIdRef.current });
    } catch (e) {
      // ignore
    }

    const tick = setInterval(() => {
      const now = Date.now();
      remoteBusyTabsRef.current.forEach((ts, id) => {
        if (now - ts >= BUSY_STATE_EXPIRY_MS) remoteBusyTabsRef.current.delete(id);
      });
      if (localBusyRef.current) broadcastBusyState(true);
      applyRingerState();
    }, BUSY_STATE_REFRESH_MS);

    return () => {
      clearInterval(tick);
      broadcastBusyState(false);
      try {
        channel.close();
      } catch (e) {
        // ignore
      }
      busyChannelRef.current = null;
    };
  }, [applyRingerState, broadcastBusyState]);

  const _cbPerms = initialState?.currentUser?.permissions;
  const _cbSuper = initialState?.currentUser?.is_super_admin;
  const hasCallBoardPermission = useCallback(() => {
    if (_cbPerms === undefined && !_cbSuper) return false;
    return _cbSuper || (_cbPerms ?? []).includes("call_board.view") || (_cbPerms ?? []).includes("call_board.manage");
  }, [_cbPerms, _cbSuper]);

  const sendHeartbeat = useCallback(async (retryCount = 0) => {
    try {
      const deviceState = deviceRef.current?.state || "unknown";
      await sendTwilioHeartbeat({
        twilio_device_state: deviceState,
        metadata: {
          page: "call_widget",
          timestamp: new Date().toISOString(),
        },
      });

      // Watchdog: if device is not registered and no active call, reconnect
      const state = deviceRef.current?.state;
      if (state && state !== "registered" && state !== "registering" && !activeCallRef.current) {
        console.warn(`Heartbeat watchdog: device in state "${state}" — triggering reconnect`);
        reconnectAttemptsRef.current = 0;
        reconnectRef.current();
      }
    } catch (error) {
      console.error(`Heartbeat error (attempt ${retryCount + 1}):`, error);
      if (retryCount < 2) {
        setTimeout(() => sendHeartbeat(retryCount + 1), 3000);
      }
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    sendHeartbeat();
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, 15000);
  }, [sendHeartbeat]);

  const stopHeartbeat = useCallback(async (reason: string) => {
    // Only call the disconnect API if heartbeat was actually running — prevents
    // double API calls when both "error" and "unregistered" fire for the same disconnect
    const wasRunning = !!heartbeatIntervalRef.current;
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (wasRunning) {
      try {
        await disconnectTwilioStatus({ reason });
      } catch (error) {
        console.error("Error disconnecting:", error);
      }
    }
  }, []);

  const stopTokenRefresh = useCallback(() => {
    if (tokenRefreshIntervalRef.current) {
      clearInterval(tokenRefreshIntervalRef.current);
      tokenRefreshIntervalRef.current = null;
    }
  }, []);

  const refreshTokenWithRetry = useCallback(async (attempt = 0) => {
    const MAX_ATTEMPTS = 3;
    const BACKOFF_MS = [2000, 4000, 8000];
    try {
      const tokenResponse = await getTwilioAccessToken();
      if (!tokenResponse?.token) {
        throw new Error(tokenResponse?.error || "Token missing in refresh response");
      }
      if (deviceRef.current) {
        deviceRef.current.updateToken(tokenResponse.token);
        console.log("Twilio token refreshed successfully");
      }
    } catch (error) {
      console.error(`Twilio token refresh failed (attempt ${attempt + 1}/${MAX_ATTEMPTS}):`, error);
      if (attempt < MAX_ATTEMPTS - 1) {
        setTimeout(() => refreshTokenWithRetry(attempt + 1), BACKOFF_MS[attempt]);
      } else {
        console.error("All token refresh attempts exhausted — heartbeat watchdog will recover once network returns.");
      }
    }
  }, []);

  // Shared reconnect scheduler — deduplication flag prevents error+unregistered
  // from both queueing a reconnect for the same underlying disconnect event.
  const scheduleReconnect = useCallback((label: string) => {
    if (activeCallRef.current) {
      console.warn(`${label}: skipping reconnect — active call in progress`);
      return;
    }
    if (reconnectScheduledRef.current) {
      console.warn(`${label}: reconnect already scheduled — skipping duplicate`);
      return;
    }
    const MAX_RECONNECT_ATTEMPTS = 5;
    const RECONNECT_BACKOFF_MS = [1000, 3000, 6000, 12000, 30000];
    const attempt = reconnectAttemptsRef.current;
    if (attempt >= MAX_RECONNECT_ATTEMPTS) {
      // All backoff attempts exhausted — start a slow offline poller (every 60s)
      // so we recover even if the browser "online" event doesn't fire reliably.
      if (!offlinePollerRef.current) {
        console.warn("Max reconnect attempts reached — starting 60s offline poller");
        offlinePollerRef.current = setInterval(() => {
          if (!deviceRef.current && !connectingRef.current && !activeCallRef.current) {
            console.log("Offline poller: retrying reconnect");
            reconnectAttemptsRef.current = 0;
            reconnectRef.current();
          }
        }, 60000);
      }
      return;
    }
    const delay = RECONNECT_BACKOFF_MS[attempt];
    reconnectAttemptsRef.current = attempt + 1;
    reconnectScheduledRef.current = true;
    console.log(`Scheduling reconnect attempt ${attempt + 1}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms (${label})`);
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      reconnectRef.current();
    }, delay);
  }, []);

  const connectTwilio = useCallback(async () => {
    if (connectingRef.current || deviceRef.current) return;
    // Don't connect when the user is on (or heading to) the login page — handles
    // the race where a scheduled reconnect fires after an auto-logout redirect.
    if (window.location.pathname.includes("/user/login")) return;
    if (!hasCallBoardPermission()) return;

    connectingRef.current = true;
    setTwilioConnecting(true);
    setTwilioReady(false);

    try {
      const tokenResponse = await getTwilioAccessToken();
      if (!tokenResponse?.token) {
        throw new Error(tokenResponse?.error || "Access token missing in response");
      }

      const device = new Device(tokenResponse.token, {
        allowIncomingWhileBusy: true,
      });

      device.on("registered", async () => {
        reconnectAttemptsRef.current = 0;
        if (offlinePollerRef.current) {
          clearInterval(offlinePollerRef.current);
          offlinePollerRef.current = null;
        }
        connectingRef.current = false;
        setTwilioReady(true);
        setTwilioConnecting(false);
        message.success("Phone service connected");

        try {
          await connectTwilioStatus({
            twilio_device_state: "registered",
            metadata: {
              page: "call_widget",
              connection_time: new Date().toISOString(),
            },
          });
          startHeartbeat();

          // Proactive token refresh every 45 min (token TTL is 1 hour)
          if (tokenRefreshIntervalRef.current) clearInterval(tokenRefreshIntervalRef.current);
          tokenRefreshIntervalRef.current = setInterval(() => refreshTokenWithRetry(0), 45 * 60 * 1000);
        } catch (error) {
          console.error("Error marking connection status:", error);
        }
      });

      device.on("error", (error: any) => {
        console.error("Twilio device error:", error);
        connectingRef.current = false;
        setTwilioReady(false);
        setTwilioConnecting(false);
        stopHeartbeat("device_error");
        stopTokenRefresh();
        // "unregistered" will also fire after most errors — scheduleReconnect
        // deduplicates so only the first caller actually queues a reconnect.
        scheduleReconnectRef.current("error");
      });

      device.on("unregistered", () => {
        connectingRef.current = false;
        setTwilioReady(false);
        setTwilioConnecting(false);
        stopTokenRefresh();
        stopHeartbeat("device_unregistered");

        // Intentional unregisters (from reconnect()) strip all listeners first,
        // so this handler only fires for unexpected events.
        // scheduleReconnect deduplicates — if "error" already queued a reconnect,
        // this call is a no-op and the counter is not incremented again.
        console.warn("Twilio device unregistered unexpectedly — scheduling reconnect");
        scheduleReconnectRef.current("unregistered");
      });

      device.on("incoming", (call: Call) => {
        console.log("Incoming call:", call);
        // Delivery receipt: this event firing is the proof that the offer
        // actually reached the CSR's browser — without it, an unanswered
        // rotation dial is a system failure, not a CSR miss.
        recordCallActivity({
          event_type: "offer_received",
          call_sid: call.parameters?.CallSid,
          customer_phone: call.customParameters?.get("customerPhone") || call.parameters?.From,
          call_source: call.customParameters?.get("callSource"),
        });
        // Add to the list. Also attach a self-cleaning listener so that if the
        // caller hangs up on their own the call is automatically removed from
        // the array without requiring any action from the CSR.
        const removeSelf = () => {
          setIncomingCalls((prev) => prev.filter((c) => c !== call));
        };
        call.on("cancel", removeSelf);
        call.on("disconnect", removeSelf);
        setIncomingCalls((prev) => [...prev, call]);
        setCallDirection("incoming");
      });

      device.on("tokenWillExpire", () => {
        console.log("Twilio tokenWillExpire — triggering fallback refresh");
        refreshTokenWithRetry(0);
      });

      await device.register();
      deviceRef.current = device;
    } catch (error) {
      console.error("Error connecting to Twilio:", error);
      connectingRef.current = false;
      setTwilioReady(false);
      setTwilioConnecting(false);

      // Auth errors are not recoverable by retrying — surface to the user and stop.
      if (
        error instanceof Error &&
        (error.message.includes("Token API error") || error.message.includes("Access token missing"))
      ) {
        message.error("Authentication error. Please check your login status.");
        return;
      }

      // Network / transient errors (ERR_CONNECTION_RESET, ERR_CONNECTION_CLOSED, etc.)
      // — schedule a retry using the same backoff as device-level reconnects so we
      // keep trying silently until the network comes back.
      scheduleReconnectRef.current("connect_failed");
    }
  }, [startHeartbeat, stopHeartbeat, refreshTokenWithRetry, stopTokenRefresh, hasCallBoardPermission]);

  const makeCall = useCallback(
    async (phoneNumber: string, queueId?: number) => {
      if (!deviceRef.current || !twilioReady) {
        message.error("Phone service not connected. Please wait or refresh.");
        return;
      }

      if (!myPhoneNumber) {
        message.error("You don't have an assigned phone number. Outbound calls are not permitted.");
        return;
      }

      try {
        const params: any = { To: phoneNumber };
        if (queueId) {
          params.queue_id = queueId.toString();
        }
        
        const call = await deviceRef.current.connect({
          params,
        });
        console.log("Outgoing call initiated:", call);
        
        call.on("accept", () => console.log("Outgoing call accepted — SID:", call.parameters?.CallSid));
        call.on("disconnect", () => console.log("Outgoing call disconnected"));
        call.on("cancel", () => console.log("Outgoing call cancelled"));
        call.on("reject", () => console.log("Outgoing call rejected"));
        call.on("error", (error: any) => {
          console.error("Outgoing call error:", error);
          const code = error?.code ?? error?.twilioError?.code;
          if (code === 31402 || error?.name === "AcquisitionFailedError") {
            message.error("Microphone access failed. Please check that your browser has microphone permission and no other app is blocking it.", 6);
          } else {
            message.error(`Call error${code ? ` (${code})` : ""}: ${error?.message || "Unknown error"}`);
          }
        });

        setActiveCall(call);
        setCallDirection("outgoing");
        message.success(`Calling ${phoneNumber}...`);
        return call;
      } catch (error: any) {
        const code = error?.code ?? error?.twilioError?.code;
        if (code === 31402 || error?.name === "AcquisitionFailedError") {
          message.error("Microphone access failed. Please check that your browser has microphone permission and no other app is blocking it.", 6);
        } else if (code === 31000 || error?.name === "GeneralErrors") {
          message.error("Phone service error. Please reconnect and try again.");
        } else {
          message.error("Failed to start call. Please try again.");
        }
        console.error("Error making call:", error);
      }
    },
    [twilioReady, myPhoneNumber],
  );

  // Keep the module-level ref in sync so out-of-tree components (e.g. CallbackBadge) can call makeCall.
  useEffect(() => {
    globalMakeCallRef.current = makeCall;
    return () => { globalMakeCallRef.current = null; };
  }, [makeCall]);

  const moveToHeld = useCallback((call: Call, holdType: "public" | "private", customerCallSid?: string) => {
    const phoneNumber = call.customParameters?.get('customerPhone') || call.parameters?.From || call.parameters?.To || "";

    // Auto-remove from heldCalls if the remote party hangs up while on hold.
    // This is the only place that listens for disconnect on a held call —
    // the active-call lifecycle effect in CallWidget detaches its listeners
    // when the call moves out of activeCall, leaving the held Call unobserved.
    const onHeldDisconnect = () => {
      setHeldCalls((prev) => prev.filter((h) => h.call !== call));
      message.warning(`Held call from ${phoneNumber || "Unknown"} was disconnected.`);
    };
    call.on("disconnect", onHeldDisconnect);
    call.on("cancel", onHeldDisconnect);

    // Do NOT call disconnect — keep the browser WebRTC leg alive inside heldCalls.
    setHeldCalls((prev) => [
      ...prev,
      { call, holdType, phoneNumber, heldAt: new Date(), customerCallSid, _disconnectHandler: onHeldDisconnect },
    ]);
    setActiveCall(null);
    // Sync the ref immediately so that any synchronous code running after
    // moveToHeld (e.g. acceptIncomingCall) sees the updated value without
    // waiting for the useEffect that normally mirrors the state into the ref.
    activeCallRef.current = null;
  }, []);

  const resumeFromHeld = useCallback((info: HeldCallInfo) => {
    recordCallActivity({
      event_type: "resume_click",
      call_sid: info.call.parameters?.CallSid,
      customer_phone: info.phoneNumber,
      call_source: info.call.customParameters?.get("callSource"),
      details: { hold_type: info.holdType, customer_call_sid: info.customerCallSid ?? null },
    });
    // Remove the auto-cleanup listener so it doesn't fire spuriously after the
    // call is restored to active and eventually ends through the normal path.
    if (info._disconnectHandler) {
      info.call.off("disconnect", info._disconnectHandler);
      info.call.off("cancel", info._disconnectHandler);
    }
    // Disconnect the current active call if one exists (should be rare; CSR
    // normally holds before accepting a new one, leaving activeCall null).
    if (activeCallRef.current) {
      try { activeCallRef.current.disconnect(); } catch (e) { /* ignore */ }
    }
    setHeldCalls((prev) => prev.filter((h) => h.call !== info.call));
    setActiveCall(info.call);
    setCallDirection("incoming");
  }, []);

  const resumeHeldCallBySid = useCallback((callSid: string): HeldCallInfo | null => {
    const current = heldCallsRef.current;

    // Match by browser-leg SID first; fall back to the customer-leg SID
    // stored for public holds so HeldCallsPanel can resolve the correct
    // HeldCallInfo regardless of hold type.
    const info = current.find(
      (h) =>
        h.call.parameters?.CallSid === callSid ||
        h.customerCallSid === callSid,
    ) ?? null;
    // Both public and private holds: promote the existing WebRTC leg back to
    // activeCall immediately.  The browser WebRTC connection was kept alive
    // inside heldCalls (moveToHeld never disconnects it), so audio resumes the
    // moment the server re-bridges the conference — no separate Accept/Decline
    // step is needed.  If the server also dials the CSR back as a new inbound
    // call, that call will arrive in incomingCalls as call-waiting and the old
    // leg will disconnect naturally when the server tears it down.
    if (info) resumeFromHeld(info);
    return info;
  }, [resumeFromHeld]);

  // Explicitly end a held call without restoring it as the active call.
  // Use this instead of resumeFromHeld when the CSR wants to drop the held leg.
  const removeFromHeld = useCallback((info: HeldCallInfo) => {
    if (info._disconnectHandler) {
      info.call.off("disconnect", info._disconnectHandler);
      info.call.off("cancel", info._disconnectHandler);
    }
    try { info.call.disconnect(); } catch (e) { /* ignore */ }
    setHeldCalls((prev) => prev.filter((h) => h.call !== info.call));
  }, []);

  const acceptIncomingCall = useCallback((call: Call) => {
    // If there is still an active call (CSR accepted without holding first),
    // disconnect it as a fallback. Normally activeCall is already null because
    // the CSR held the call via moveToHeld before accepting the new leg.
    if (activeCallRef.current) {
      try { activeCallRef.current.disconnect(); } catch (e) { /* ignore */ }
      setActiveCall(null);
    }
    call.accept();
    setActiveCall(call);
    setIncomingCalls((prev) => prev.filter((c) => c !== call));
  }, []);

  const rejectIncomingCall = useCallback((call: Call) => {
    try { call.reject(); } catch (e) { /* ignore */ }
    setIncomingCalls((prev) => {
      const next = prev.filter((c) => c !== call);
      if (next.length === 0 && !activeCall) setCallDirection(null);
      return next;
    });
  }, [activeCall]);

  const hangupCall = useCallback(() => {
    if (activeCallRef.current) {
      try { activeCallRef.current.disconnect(); } catch (e) { /* ignore */ }
      setActiveCall(null);
    }
    // Disconnect every held call and remove their auto-cleanup listeners first
    // so onHeldDisconnect does not fire for each one during bulk teardown.
    heldCallsRef.current.forEach(({ call, _disconnectHandler }) => {
      if (_disconnectHandler) {
        call.off("disconnect", _disconnectHandler);
        call.off("cancel", _disconnectHandler);
      }
      try { call.disconnect(); } catch (e) { /* ignore */ }
    });
    setHeldCalls([]);
    // Reject every waiting incoming call
    incomingCallsRef.current.forEach((call) => {
      const status = call.status();
      try {
        if (status === "pending") call.reject();
        else if (status !== "closed") call.disconnect();
      } catch (e) { /* ignore */ }
    });
    setIncomingCalls([]);
    setCallDirection(null);
  }, []);

  const clearIncomingCall = useCallback((call: Call) => {
    setIncomingCalls((prev) => {
      const next = prev.filter((c) => c !== call);
      if (next.length === 0 && !activeCall) setCallDirection(null);
      return next;
    });
  }, [activeCall]);

  const clearActiveCall = useCallback(() => {
    setActiveCall(null);
    setCallDirection(null);
  }, []);

  const reconnect = useCallback(() => {
    if (connectingRef.current) return;
    connectingRef.current = true;
    // Do NOT reset reconnectAttemptsRef here — backoff must keep growing across
    // consecutive failures (network down). It is reset only in the "registered" handler.
    reconnectScheduledRef.current = false; // allow next failure to schedule the next attempt
    // Stop heartbeat before destroying the device so the DB doesn't show
    // is_connected=true while the device is being rebuilt.
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (deviceRef.current) {
      try {
        deviceRef.current.removeAllListeners(); // strip all listeners before intentional unregister
        if (deviceRef.current.state === "registered") {
          deviceRef.current.unregister().catch(() => {}); // async — swallow rejection
        }
        deviceRef.current.destroy();
      } catch (e) {
        // ignore
      }
      deviceRef.current = null;
    }
    stopTokenRefresh();
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    connectingRef.current = false;
    setTwilioReady(false);
    setTwilioConnecting(false);
    // Small delay lets the old WebSocket fully close before the new Device opens one
    setTimeout(() => connectTwilio(), 1000);
  }, [connectTwilio, stopTokenRefresh]);

  // Stable refs so effects don't re-run when callbacks are recreated
  const connectTwilioRef = useRef(connectTwilio);
  connectTwilioRef.current = connectTwilio;
  reconnectRef.current = reconnect;
  scheduleReconnectRef.current = scheduleReconnect;

  // Initialize once on mount (only if user has call board permission)
  useEffect(() => {
    if (!hasCallBoardPermission()) return;

    registerTab();

    getMyTwilioNo()
      .then((res) => {
        if (res?.phone_number) setMyPhoneNumber(res.phone_number);
      })
      .catch((err) => console.error("Error fetching phone number:", err));

    const timer = setTimeout(() => connectTwilioRef.current(), 1000);

    return () => {
      clearTimeout(timer);

      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
        tokenRefreshIntervalRef.current = null;
      }

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      if (offlinePollerRef.current) {
        clearInterval(offlinePollerRef.current);
        offlinePollerRef.current = null;
      }

      if (deviceRef.current) {
        try {
          deviceRef.current.removeAllListeners("incoming");
          deviceRef.current.calls.forEach((call) => {
            try {
              if (call.status() === "pending") call.reject();
              else if (call.status() !== "closed") call.disconnect();
            } catch (e) {
              // ignore
            }
          });
          deviceRef.current.unregister();
          deviceRef.current.destroy();
        } catch (e) {
          console.error("Error destroying Twilio device:", e);
        }
        deviceRef.current = null;
      }

      if (unregisterTab() && !disconnectBeaconSentRef.current) {
        disconnectBeaconSentRef.current = true;
        try {
          const data = JSON.stringify({ reason: "provider_unmount" });
          const blob = new Blob([data], { type: "application/json" });
          navigator.sendBeacon("/tenant/twilio/connection/disconnect", blob);
        } catch (e) {
          // best effort
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCallBoardPermission]);

  // Tear down the device immediately when the user navigates to the login page.
  // This covers auto-logout paths (e.g. 403 from requestErrorConfig) that redirect
  // to /user/login without clearing currentUser from React state, which would
  // otherwise leave the Twilio device registered and the CallWidget ringing.
  useEffect(() => {
    const unlisten = history.listen(({ location }) => {
      if (!location.pathname.includes("/user/login")) return;

      // Cancel pending reconnect timers so we don't re-register after teardown.
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (offlinePollerRef.current) {
        clearInterval(offlinePollerRef.current);
        offlinePollerRef.current = null;
      }
      reconnectScheduledRef.current = false;
      connectingRef.current = false;

      stopHeartbeat("navigate_to_login");
      stopTokenRefresh();

      if (deviceRef.current) {
        try {
          deviceRef.current.removeAllListeners();
          if (deviceRef.current.state === "registered") {
            deviceRef.current.unregister().catch(() => {});
          }
          deviceRef.current.destroy();
        } catch (e) {
          // ignore
        }
        deviceRef.current = null;
      }

      setTwilioReady(false);
      setTwilioConnecting(false);
      setIncomingCalls([]);
      setActiveCall(null);
      setHeldCalls([]);
      setCallDirection(null);
    });
    return unlisten;
  }, [stopHeartbeat, stopTokenRefresh]);

  // Handle browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (twilioReady && unregisterTab() && !disconnectBeaconSentRef.current) {
        disconnectBeaconSentRef.current = true;
        const data = JSON.stringify({ reason: "page_unload" });
        const blob = new Blob([data], { type: "application/json" });
        navigator.sendBeacon("/tenant/twilio/connection/disconnect", blob);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [twilioReady, unregisterTab]);

  // Handle visibility change - restart heartbeat interval when page becomes visible
  // so the interval is always aligned to when the last heartbeat actually fired.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && twilioReady) {
        startHeartbeat();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [twilioReady, startHeartbeat]);

  // Handle network online/offline
  useEffect(() => {
    const handleOnline = () => {
      const deviceState = deviceRef.current?.state;
      if (!deviceRef.current || deviceState === "unregistered" || deviceState === "destroyed") {
        // Network came back but device is gone — reset attempt counter so the
        // backoff schedule starts fresh, then reconnect unconditionally.
        console.log("Network online: device not registered — triggering reconnect");
        reconnectAttemptsRef.current = 0;
        reconnectRef.current();
        return;
      }
      if (twilioReady) {
        connectTwilioStatus({
          twilio_device_state: deviceState || "registered",
          metadata: { page: "call_widget", reconnection: true, timestamp: new Date().toISOString() },
        }).catch((err) => console.error("Error reconnecting:", err));

        if (!heartbeatIntervalRef.current) startHeartbeat();
      }
    };

    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [twilioReady, startHeartbeat]);

  // If user has no call board permission, just render children without Twilio
  if (!hasCallBoardPermission()) {
    return <>{children}</>;
  }

  return (
    <TwilioDeviceContext.Provider
      value={{
        device: deviceRef.current,
        twilioReady,
        twilioConnecting,
        myPhoneNumber,
        incomingCalls,
        activeCall,
        heldCalls,
        callDirection,
        makeCall,
        acceptIncomingCall,
        rejectIncomingCall,
        hangupCall,
        clearIncomingCall,
        clearActiveCall,
        moveToHeld,
        resumeFromHeld,
        resumeHeldCallBySid,
        removeFromHeld,
        reconnect,
      }}
    >
      {children}
    </TwilioDeviceContext.Provider>
  );
};
